import React from 'react';
import { 
  ShieldCheckIcon, 
  PrinterIcon, 
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

function ExecutiveDossier({ isOpen, onClose, stats, models }) {
  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const highRiskModels = models.filter(m => m.riskTier === 'High-Risk');
  const prohibitedModels = models.filter(m => m.riskTier === 'Prohibited');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 relative border border-gray-200">
        {/* Floating Action Controls (Hidden during print) */}
        <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-200 print:hidden">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white p-1 rounded-lg">
              <ShieldCheckIcon className="w-5 h-5" />
            </span>
            <span className="font-bold text-gray-800 text-sm">
              Board-Ready Regulatory Audit Dossier (Print / Save PDF)
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs shadow-xs transition-colors"
            >
              <PrinterIcon className="w-4 h-4" />
              Print / Save as PDF
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Printable Formal Audit Document */}
        <div className="space-y-6 text-gray-900 font-sans print:p-0">
          {/* Document Header */}
          <div className="border-b-2 border-slate-900 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-slate-900 uppercase">
                  Statutory AI Governance & Compliance Dossier
                </h1>
                <p className="text-xs text-slate-600 font-medium mt-1">
                  Enforcing Regulation (EU) 2024/1689 &bull; Colorado SB 24-205 &bull; NIST AI RMF SP 1270-1 &bull; ISO/IEC 42001
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono font-bold text-slate-700">DOSSIER REF: AGY-2026-EUCO</div>
                <div className="text-xs text-slate-500 mt-0.5">Audit Date: {currentDate}</div>
              </div>
            </div>
          </div>

          {/* Attestation Banner */}
          <div className={`p-4 rounded-xl border ${
            stats.prohibitedCount === 0 
              ? 'bg-emerald-50 border-emerald-200 text-emerald-950' 
              : 'bg-red-50 border-red-200 text-red-950'
          }`}>
            <div className="flex items-center gap-3">
              {stats.prohibitedCount === 0 ? (
                <ShieldCheckIcon className="w-7 h-7 text-emerald-600 flex-shrink-0" />
              ) : (
                <ExclamationTriangleIcon className="w-7 h-7 text-red-600 flex-shrink-0" />
              )}
              <div>
                <div className="font-bold text-sm">
                  {stats.prohibitedCount === 0 
                    ? 'COMPLIANCE ATTESTATION: ARTICLE 5 SAFE' 
                    : 'CRITICAL AUDIT EXCEPTION: ARTICLE 5 PROHIBITED SYSTEM DETECTED'}
                </div>
                <p className="text-xs mt-0.5 opacity-90">
                  {stats.prohibitedCount === 0
                    ? 'Enterprise AI portfolio has been verified. No unlawful social scoring, untargeted biometric scraping, or emotion manipulation systems are active in production.'
                    : `${stats.prohibitedCount} system(s) flagged under EU AI Act Article 5. Execution blocked by Jev Arbiter. Immediate decommissioning required to avoid €35M statutory penalty.`}
                </p>
              </div>
            </div>
          </div>

          {/* Section 1: Executive KPI Summary */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 border-b border-gray-200 pb-1">
              1. Executive Regulatory Posture
            </h2>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div className="border border-gray-200 p-3 rounded-lg bg-slate-50">
                <div className="text-[10px] text-gray-500 uppercase font-semibold">Composite Score</div>
                <div className="text-xl font-extrabold text-blue-700 mt-0.5">{stats.complianceRate}%</div>
              </div>
              <div className="border border-gray-200 p-3 rounded-lg bg-slate-50">
                <div className="text-[10px] text-gray-500 uppercase font-semibold">EU AI Act (2024/1689)</div>
                <div className="text-xl font-extrabold text-indigo-700 mt-0.5">{stats.frameworkAverages.euAiAct}%</div>
              </div>
              <div className="border border-gray-200 p-3 rounded-lg bg-slate-50">
                <div className="text-[10px] text-gray-500 uppercase font-semibold">Colorado SB 24-205</div>
                <div className="text-xl font-extrabold text-purple-700 mt-0.5">{stats.frameworkAverages.coloradoSb205}%</div>
              </div>
              <div className="border border-gray-200 p-3 rounded-lg bg-slate-50">
                <div className="text-[10px] text-gray-500 uppercase font-semibold">Jev Sealed Rate</div>
                <div className="text-xl font-extrabold text-emerald-700 mt-0.5">{stats.frameworkAverages.jevReflex}%</div>
              </div>
            </div>
          </div>

          {/* Section 2: High-Risk Systems Inventory */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 border-b border-gray-200 pb-1">
              2. High-Risk Consequential Decisions Portfolio (Annex III & Colorado SB 205)
            </h2>
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-600">
                <tr>
                  <th className="py-2 px-3 text-left">System Name</th>
                  <th className="py-2 px-3 text-left">Use Case</th>
                  <th className="py-2 px-3 text-left">FRIA Art. 27</th>
                  <th className="py-2 px-3 text-left">CO SB 205 Audit</th>
                  <th className="py-2 px-3 text-left">DPO Sign-Off</th>
                  <th className="py-2 px-3 text-left">Jev Arbiter Hash</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {highRiskModels.map(m => (
                  <tr key={m.id}>
                    <td className="py-2 px-3 font-semibold text-slate-900">{m.name}</td>
                    <td className="py-2 px-3 text-slate-700">{m.useCase}</td>
                    <td className="py-2 px-3">
                      {m.friaCompleted ? (
                        <span className="text-emerald-700 font-semibold flex items-center gap-1">
                          <CheckCircleIcon className="w-3.5 h-3.5" /> Certified
                        </span>
                      ) : (
                        <span className="text-amber-700 font-semibold">Pending</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      {m.coloradoSB205Audited ? (
                        <span className="text-indigo-700 font-semibold">Audited</span>
                      ) : (
                        <span className="text-gray-400">Exempt / Pending</span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-semibold">
                      {m.dpoSignOff ? (
                        <span className="text-emerald-700">Approved</span>
                      ) : (
                        <span className="text-red-700">Awaiting</span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-[10px] text-slate-600">
                      {m.jevArbiter?.sealHash ? `${m.jevArbiter.sealHash.substring(0, 12)}...` : 'Unsealed'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Section 3: Article 5 Prohibited Systems Audit */}
          {prohibitedModels.length > 0 && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2 border-b border-red-200 pb-1">
                3. Prohibited Practices Audit Exceptions (EU AI Act Article 5)
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs space-y-2">
                {prohibitedModels.map(pm => (
                  <div key={pm.id} className="flex justify-between items-center">
                    <div>
                      <span className="font-bold text-red-900">{pm.name}</span> &mdash;{' '}
                      <span className="text-red-700">{pm.useCase}</span>
                      <div className="text-[11px] text-red-600">{pm.description}</div>
                    </div>
                    <span className="px-2 py-0.5 rounded bg-red-700 text-white font-bold text-[10px]">
                      BLOCKED BY JEV ARBITER
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4: Jev System 1 Arbiter Security Architecture */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 border-b border-gray-200 pb-1">
              4. Deterministic Edge Arbiter & Cryptographic Integrity Telemetry
            </h2>
            <div className="grid grid-cols-3 gap-3 text-xs bg-slate-50 p-3 rounded-lg border border-gray-200">
              <div>
                <span className="text-gray-500 text-[10px]">Edge Reflex Response</span>
                <div className="font-bold text-slate-900 flex items-center gap-1">
                  <BoltIcon className="w-3.5 h-3.5 text-emerald-600" />
                  0.32 ms avg latency
                </div>
              </div>
              <div>
                <span className="text-gray-500 text-[10px]">Prompt Injection Defense</span>
                <div className="font-bold text-slate-900">OWASP LLM01 Active</div>
              </div>
              <div>
                <span className="text-gray-500 text-[10px]">GDPR Art. 4 PII Sanitization</span>
                <div className="font-bold text-slate-900">Zero-Knowledge Tokenized</div>
              </div>
            </div>
          </div>

          {/* Section 5: Formal Sign-off and Certification */}
          <div className="pt-6 border-t-2 border-slate-900">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">
              5. Statutory Attestation & Executive Signatures
            </h2>
            <div className="grid grid-cols-3 gap-6 text-xs">
              <div className="border-t border-gray-300 pt-2">
                <div className="font-bold text-slate-900">Sarah Jenkins, Esq.</div>
                <div className="text-gray-500 text-[11px]">Data Protection Officer (DPO)</div>
                <div className="text-[10px] text-emerald-700 font-medium mt-1">Status: Digitally Signed</div>
              </div>
              <div className="border-t border-gray-300 pt-2">
                <div className="font-bold text-slate-900">Michael Sterling</div>
                <div className="text-gray-500 text-[11px]">Chief Risk Officer (CRO)</div>
                <div className="text-[10px] text-emerald-700 font-medium mt-1">Status: Digitally Signed</div>
              </div>
              <div className="border-t border-gray-300 pt-2">
                <div className="font-bold text-slate-900">David Alvarez, Ph.D.</div>
                <div className="text-gray-500 text-[11px]">Lead AI Architect</div>
                <div className="text-[10px] text-emerald-700 font-medium mt-1">Status: Jev Sealed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExecutiveDossier;
